import * as React from "react";
import { Slider as BaseSlider, sliderClasses } from "@mui/base/Slider";
import { styled, alpha, Box } from "@mui/system";
import { CircularDiv, InlineDiv } from "../divs";
import { LightText, RegularText } from "../texts";
import { ActivitiesRef } from "@/types/components";
import { PRIMARY_BUTTON_COLOR } from "@/utils/constants";

interface SliderProps {
  valuesState?: ActivitiesRef;
  deleteAll?: boolean;
}

const INITIAL_DISTANCE = 50;
const MAX_DISTANCE = 500;
const MIN_DISTANCE = 1;

export const DistanceSlider: React.FC<SliderProps> = ({
  valuesState,
  deleteAll,
}) => {
  const [value, setValue] = React.useState<number>(
    valuesState?.current?.distance ?
      valuesState.current.distance / 1000
    : INITIAL_DISTANCE,
  );

  React.useEffect(() => {
    if (deleteAll) {
      setValue(INITIAL_DISTANCE);
    }
  }, [deleteAll]);
  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number);
    const parsedDistance = newValue as number;
    if (valuesState) {
      valuesState.current = {
        ...valuesState.current,
        distance: parsedDistance * 1000,
      };
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Slider
        value={value}
        onChange={handleChange}
        getAriaLabel={() => "Distance range"}
        getAriaValueText={valuetext}
        slots={{ valueLabel: SliderValueLabel }}
        min={MIN_DISTANCE}
        max={MAX_DISTANCE}
      />
      <InlineDiv
        style={{ justifyContent: "space-between", marginTop: "-10px" }}
      >
        <LightText
          fontSize="14px"
          style={{ marginTop: "7px", marginRight: "4px" }}
        >
          {MIN_DISTANCE} km
        </LightText>

        <LightText
          fontSize="14px"
          style={{ marginTop: "7px", marginRight: "4px" }}
        >
          {MAX_DISTANCE} km
        </LightText>
      </InlineDiv>
    </Box>
  );
};

interface SliderValueLabelProps {
  children: React.ReactElement;
}

function SliderValueLabel({ children }: SliderValueLabelProps) {
  return (
    <CircularDiv
      background={PRIMARY_BUTTON_COLOR}
      paddingX="10px"
      height="30px"
      style={{ marginBottom: "50px" }}
    >
      <RegularText fontSize="14px">{children}</RegularText>
    </CircularDiv>
  );
}

function valuetext(value: number) {
  return `${value}°C`;
}

const blue = {
  100: "#DAECFF",
  200: "#99CCF3",
  400: "#3399FF",
  300: "#66B2FF",
  500: "#007FFF",
  600: "#0072E5",
  700: "#0059B3",
  900: "#003A75",
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Slider = styled(BaseSlider)(
  ({ theme }) => `
  color: ${theme.palette.mode === "light" ? blue[500] : blue[400]};
  height: 6px;
  width: 100%;
  padding: 16px 0;
  display: inline-flex;
  align-items: center;
  position: relative;
  cursor: pointer;
  touch-action: none;
  -webkit-tap-highlight-color: transparent;

  &.${sliderClasses.disabled} {
    pointer-events: none;
    cursor: default;
    color: ${theme.palette.mode === "light" ? grey[300] : grey[600]};
    opacity: 0.4;
  }

  & .${sliderClasses.rail} {
    display: block;
    position: absolute;
    width: 100%;
    height: 3px;
    border-radius: 6px;
    background-color: #FFF7E4;
    opacity: 1;
  }

  & .${sliderClasses.track} {
    display: block;
    position: absolute;
    height: 3px;
    border-radius: 6px;
    background-color: #FFCD4D;
  }

  & .${sliderClasses.thumb} {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    margin-left: -6px;
    width: 10px;
    height: 10px;
    box-sizing: border-box;
    border-radius: 50%;
    outline: 0;
    background-color: #FFCD4D;
    transition-property: box-shadow, transform;
    transition-timing-function: ease;
    transition-duration: 120ms;
    transform-origin: center;

    &:hover {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      )};
    }

    &.${sliderClasses.focusVisible} {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      outline: none;
    }

    &.${sliderClasses.active} {
      box-shadow: 0 0 0 8px ${alpha("#FFCD4D", 0.5)};
      outline: none;
      transform: scale(1.2);
    }
  }

  & .${sliderClasses.mark} {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 99%;
    background-color: #FFCD4D;
    top: 44%;
    transform: translateX(-50%);
  }

  & .${sliderClasses.markActive} {
    background-color: #FFCD4D};
  }
`,
);

export default DistanceSlider;
